import React, { useState, useEffect, useRef } from "react";
import { FiX, FiUpload } from "react-icons/fi";
import "./CareerModal.css";

const CareerModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    currentRole: "",
    areaOfInterest: "",
    experience: "",
    linkedinUrl: "",
    resume: null,
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    if (!formData.currentRole.trim()) {
      newErrors.currentRole = "Current role is required";
    }

    if (!formData.areaOfInterest) {
      newErrors.areaOfInterest = "Please select an area of interest";
    }

    if (!formData.experience) {
      newErrors.experience = "Please select your experience level";
    }

    if (formData.resume) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(formData.resume.type)) {
        newErrors.resume = "Please upload PDF, DOC, or DOCX file";
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = "Please add a short message";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, resume: file }));
    if (errors.resume) {
      setErrors((prev) => ({ ...prev, resume: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    // Simulate API call - replace with actual API endpoint
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("career-modal-overlay")) {
      onClose();
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      currentRole: "",
      areaOfInterest: "",
      experience: "",
      linkedinUrl: "",
      resume: null,
      message: "",
    });
    setErrors({});
    setIsSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="career-modal-overlay" onClick={handleOverlayClick}>
      <div className="career-modal" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="career-modal-title">
        <button className="career-modal-close" onClick={handleClose} aria-label="Close modal">
          <FiX />
        </button>

        {isSubmitted ? (
          <div className="career-modal-success">
            <div className="success-icon">✓</div>
            <h3>Application Submitted Successfully</h3>
            <p>
              Thank you for your interest in Data Bolta Hai. Our team will review
              your details and get back to you.
            </p>
            <button className="career-modal-btn-primary" onClick={handleClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="career-modal-header">
              <h3 id="career-modal-title">Connect With Us</h3>
              <p>
                Interested in joining our team? Share your details and we'll get
                in touch.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="career-modal-form">
              <div className="career-form-row">
                <div className="career-form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input
                    ref={firstInputRef}
                    id="fullName"
                    type="text"
                    name="fullName"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={errors.fullName ? "error" : ""}
                  />
                  {errors.fullName && <span className="career-form-error">{errors.fullName}</span>}
                </div>

                <div className="career-form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? "error" : ""}
                  />
                  {errors.email && <span className="career-form-error">{errors.email}</span>}
                </div>
              </div>

              <div className="career-form-row">
                <div className="career-form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    placeholder="9876543210"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? "error" : ""}
                  />
                  {errors.phone && <span className="career-form-error">{errors.phone}</span>}
                </div>

                <div className="career-form-group">
                  <label htmlFor="currentRole">Current Role / Qualification *</label>
                  <input
                    id="currentRole"
                    type="text"
                    name="currentRole"
                    placeholder="e.g., Software Engineer"
                    value={formData.currentRole}
                    onChange={handleInputChange}
                    className={errors.currentRole ? "error" : ""}
                  />
                  {errors.currentRole && <span className="career-form-error">{errors.currentRole}</span>}
                </div>
              </div>

              <div className="career-form-row">
                <div className="career-form-group">
                  <label htmlFor="areaOfInterest">Area of Interest *</label>
                  <select
                    id="areaOfInterest"
                    name="areaOfInterest"
                    value={formData.areaOfInterest}
                    onChange={handleInputChange}
                    className={errors.areaOfInterest ? "error" : ""}
                  >
                    <option value="">Select area</option>
                    <option value="Data Analytics">Data Analytics</option>
                    <option value="AI / Machine Learning">AI / Machine Learning</option>
                    <option value="Software Development">Software Development</option>
                    <option value="Business Development">Business Development</option>
                    <option value="Finance / Operations">Finance / Operations</option>
                    <option value="UI/UX Design">UI/UX Design</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.areaOfInterest && <span className="career-form-error">{errors.areaOfInterest}</span>}
                </div>

                <div className="career-form-group">
                  <label htmlFor="experience">Experience *</label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className={errors.experience ? "error" : ""}
                  >
                    <option value="">Select experience</option>
                    <option value="Fresher">Fresher</option>
                    <option value="0-2 Years">0–2 Years</option>
                    <option value="2-5 Years">2–5 Years</option>
                    <option value="5+ Years">5+ Years</option>
                  </select>
                  {errors.experience && <span className="career-form-error">{errors.experience}</span>}
                </div>
              </div>

              <div className="career-form-group">
                <label htmlFor="linkedinUrl">LinkedIn / Portfolio URL (optional)</label>
                <input
                  id="linkedinUrl"
                  type="url"
                  name="linkedinUrl"
                  placeholder="https://linkedin.com/in/yourprofile"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                />
              </div>

              <div className="career-form-group">
                <label htmlFor="resume">Resume Upload (PDF/DOC/DOCX)</label>
                <div className={`career-file-input ${errors.resume ? "error" : ""}`}>
                  <FiUpload className="file-icon" />
                  <span>{formData.resume ? formData.resume.name : "Choose file"}</span>
                  <input
                    id="resume"
                    type="file"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </div>
                {errors.resume && <span className="career-form-error">{errors.resume}</span>}
              </div>

              <div className="career-form-group">
                <label htmlFor="message">Short Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="3"
                  placeholder="Tell us why you'd like to join..."
                  value={formData.message}
                  onChange={handleInputChange}
                  className={errors.message ? "error" : ""}
                />
                {errors.message && <span className="career-form-error">{errors.message}</span>}
              </div>

              <button
                type="submit"
                className="career-modal-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Application →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default CareerModal;
